<template>
  <v-btn-toggle
    :value="dark ? 1 : 0"
    @change="onSelect"
    mandatory
    dense
    rounded
    class="theme-toggle"
    :class="dark ? 'theme-toggle--dark' : 'theme-toggle--light'"
  >
    <v-btn
      :value="0"
      small
      :aria-label="$t('theme.light')"
      :title="$t('theme.light')"
    >
      <v-icon small color="white">mdi-weather-sunny</v-icon>
    </v-btn>
    <v-btn
      :value="1"
      small
      :aria-label="$t('theme.dark')"
      :title="$t('theme.dark')"
    >
      <v-icon small color="white">mdi-weather-night</v-icon>
    </v-btn>
  </v-btn-toggle>
</template>

<script lang="ts">
import Vue from "vue";
import Component from "vue-class-component";
import { Emit, Prop, Watch } from "vue-property-decorator";

@Component
export default class ThemeSwitch extends Vue {
  @Prop({ type: Boolean, required: true })
  public readonly dark!: boolean;

  @Emit("update:dark")
  public emitUpdateDark(dark: boolean) {
    return dark;
  }

  // Apply the incoming prop to Vuetify only — do NOT emit here, otherwise a
  // parent-driven update would echo back as update:dark (and trigger a
  // redundant store commit / double-emit on user actions).
  @Watch("dark", { immediate: true })
  public onDarkChange(dark: boolean) {
    this.$vuetify.theme.dark = dark;
  }

  // User interaction: apply AND emit so the parent (and store) stay in sync.
  public onSelect(index: number) {
    this.switchMode(index === 1);
  }

  public switchMode(value: boolean | null) {
    this.$vuetify.theme.dark = value ?? false;
    this.emitUpdateDark(value ?? false);
  }
}
</script>

<style scoped lang="scss">
.theme-toggle.v-btn-toggle {
  position: relative;
  border-radius: 999px;
  padding: 2px;

  // pastille coulissante : c'est elle le fond de la sélection, elle glisse d'un segment à l'autre
  &::before {
    content: "";
    position: absolute;
    z-index: 0;
    top: 2px;
    bottom: 2px;
    left: 2px;
    width: 40px;
    border-radius: 999px;
    background-color: #ffffff;
    transition: transform 0.25s ease, background-color 0.25s ease;
  }

  ::v-deep .v-btn {
    position: relative;
    z-index: 1; // au-dessus de la pastille
    width: 40px;
    min-width: 40px !important;
    height: 30px;
    padding: 0 !important;
    border: none !important;
    border-radius: 999px !important;
    background-color: transparent !important;

    // neutralise l'overlay/dim par défaut de Vuetify
    &::before {
      opacity: 0 !important;
    }
  }

  // icône du segment sélectionné en orange (transition douce avec le glissement)
  ::v-deep .v-icon {
    transition: color 0.25s ease;
  }
  ::v-deep .v-btn--active .v-icon {
    color: #ff6a00 !important;
  }
}

// Mode clair (barre orange) : conteneur peu contrasté.
// !important indispensable pour battre la règle Vuetify
// `.theme--dark.v-btn-toggle:not(.v-btn-toggle--group) { background:#1E1E1E }`
// appliquée à cause du <v-app-bar dark> — sinon le fond reste tout noir.
.theme-toggle--light.v-btn-toggle {
  background-color: rgba(0, 0, 0, 0.2) !important;
}

// Mode sombre : conteneur sombre par défaut de Vuetify (#1E1E1E) ;
// pastille glissée à droite (segment lune) et blanc adouci à 50% d'opacité.
.theme-toggle--dark.v-btn-toggle::before {
  transform: translateX(40px);
  background-color: rgba(255, 255, 255, 0.25);
}
</style>
